import { useCallback, useEffect, useRef, useState } from "react";
import { MdMyLocation } from "react-icons/md";

import style from "./PlacesInput.module.scss";
import {
    useAutocomplete,
    useLazyReverseGeocodeQuery,
} from "../../../api/endpoints/places";
import useDeviceLocation, {
    isPreciseEnoughToName,
} from "../../../hooks/useDeviceLocation";
import type { Point } from "../../../api/types";

/**
 * An address box backed by the API's Places endpoints.
 *
 * It used to run `google.maps.places.Autocomplete` in the browser, which had
 * three problems beyond the billing. It restricted results to
 * `country: "US"` in an app that prices rides in naira, so a Lagos address
 * could not be typed at all. It looked its input up with
 * `document.getElementById` inside an effect and never removed the listener it
 * attached, so remounting the form stacked another one. And its `value` prop
 * flipped between a string and `undefined`, which switches React between a
 * controlled and an uncontrolled input mid-life.
 *
 * Going through the API instead keeps the Places key server-side, lets the API
 * cache repeated lookups, and gives the web the same suggestions the mobile
 * clients get.
 *
 * **It searches whether or not anyone is signed in.** The landing page asks a
 * visitor where they are travelling before it asks them to sign up, so this box
 * is the first thing on the site they touch. It used to stay silent when signed
 * out, because `/places/*` was behind the API's auth guard and every keystroke
 * would have been a 401; that reads as a broken field, not as a feature held
 * back for members. The endpoint is now open and rate limited per address
 * instead, so the only difference a session makes is a larger allowance.
 *
 * **It searches the world.** The default used to be `countries="ng"`, which is
 * a hard filter and not a bias: a passenger in Accra or London typed their own
 * street and was told there were no matching places. Nigeria is where the
 * service started, not where its addresses end, and the position bias below
 * already floats local results to the top without excluding anything.
 */

export interface PlaceSelection {
    name: string;
    address: string;
    latitude: number;
    longitude: number;
}

interface Props {
    id: string;
    name: string;
    placeholder: string;
    className?: string;
    value?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /** Called with the resolved place once a suggestion is chosen. */
    setter: (selection: PlaceSelection) => void;
    /** Biases suggestions towards the user when their position is known. */
    origin?: Point;
    /**
     * ISO country codes, comma separated. Undefined searches worldwide, which
     * is the default; pass one only where the field genuinely is restricted.
     */
    countries?: string;
    /**
     * Offers "use my current location" at the top of the list. On by default,
     * and worth turning off for a destination: nobody travels to where they
     * already are.
     */
    offerCurrentLocation?: boolean;
    required?: boolean;
    /**
     * Flashes the confirmation ring once on mount, for a value that arrived
     * with the person rather than being chosen here.
     *
     * The ring already means "that landed in this box". A panel that seeds
     * itself from an earlier screen has the same thing to say and no gesture to
     * hang it on, and a box that is simply full when the panel opens is a box
     * the eye skips. Ignored when there is nothing in the field: a ring around
     * an empty box announces nothing.
     */
    highlight?: boolean;
}

const PlacesInput = ({
    id,
    name,
    placeholder,
    className,
    value,
    onChange,
    setter,
    origin,
    countries,
    offerCurrentLocation = true,
    required = true,
    highlight = false,
}: Props) => {
    const [reverseGeocode] = useLazyReverseGeocodeQuery();
    const device = useDeviceLocation();

    // The device's own fix beats the caller's guess as a search bias: the
    // caller's guess is usually the other end of the journey.
    const bias: Point | undefined = device.fix
        ? { longitude: device.fix.longitude, latitude: device.fix.latitude }
        : origin;

    const { query, search, resolve, suggestions, isFetching } = useAutocomplete({
        origin: bias,
        countries,
    });

    const [open, setOpen] = useState(false);
    const [resolving, setResolving] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    /** Which row the keyboard is on. -1 is "none, the input has the caret". */
    const [activeIndex, setActiveIndex] = useState(-1);
    /** Flashes the box green for a moment after a place lands in it. */
    const [justFilled, setJustFilled] = useState(
        () => highlight && Boolean(value)
    );
    const container = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    // A controlled input either way: `text` is the source of truth, seeded from
    // `value` when the parent supplies one. Mixing the two is what produced the
    // controlled/uncontrolled warning in the previous version.
    //
    // Synced during render rather than in an effect. React's documented way to
    // adjust state when a prop changes: an effect would render once with the
    // stale text, then again with the new one, and the user would see the old
    // address flash back before being replaced.
    const [text, setText] = useState(value ?? "");
    const [seenValue, setSeenValue] = useState(value);
    if (value !== undefined && value !== seenValue) {
        setSeenValue(value);
        setText(value);
    }

    // Clicking away closes the list. Bound to the document rather than relying
    // on blur, because blur fires before the click on a suggestion registers
    // and would close the list out from under the selection.
    useEffect(() => {
        if (!open) return;
        const onDocumentClick = (event: MouseEvent) => {
            if (!container.current?.contains(event.target as Node)) {
                setOpen(false);
                setActiveIndex(-1);
            }
        };
        document.addEventListener("mousedown", onDocumentClick);
        return () => document.removeEventListener("mousedown", onDocumentClick);
    }, [open]);

    // The confirmation flash is a moment, not a state: it says "that landed"
    // and then gets out of the way.
    useEffect(() => {
        if (!justFilled) return;
        const timer = window.setTimeout(() => setJustFilled(false), 900);
        return () => window.clearTimeout(timer);
    }, [justFilled]);

    /** Rows above the suggestions, so the keyboard can count them. */
    const leadingRows = offerCurrentLocation ? 1 : 0;
    const rowCount = leadingRows + suggestions.length;
    const showList = open && rowCount > 0;

    const fill = useCallback(
        (selection: PlaceSelection, label: string) => {
            setter(selection);
            setText(label);
            setJustFilled(true);
            setNotice(null);
            setOpen(false);
            setActiveIndex(-1);
        },
        [setter]
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setText(event.target.value);
        onChange?.(event);
        search(event.target.value);
        setNotice(null);
        setActiveIndex(-1);
        setOpen(true);
    };

    const choose = async (placeId: string, description: string) => {
        // Shown immediately: the details call is a round trip, and leaving the
        // half-typed text in the box until it returns looks like the click was
        // ignored.
        setText(description);
        setOpen(false);
        setResolving(true);
        try {
            const place = await resolve(placeId);
            fill(
                {
                    name: place.name,
                    address: place.address,
                    latitude: place.location.latitude,
                    longitude: place.location.longitude,
                },
                place.address || description
            );
        } catch {
            // Put the box back the way it was so the person can pick again.
            // No toast: the list is still there and retrying is one click.
            setText(text);
            setOpen(true);
        } finally {
            setResolving(false);
        }
    };

    /**
     * Names where the browser says the person is, and puts it in the box.
     *
     * Refuses a fix too coarse to name rather than filling the field with a
     * plausible-looking street the driver would be sent to. The same rule the
     * Flutter picker applies, for the same reason: the passenger cannot tell a
     * cell-tower guess from a GPS fix by reading the address it produced.
     */
    const fillFromCurrentLocation = async () => {
        setNotice(null);
        const { fix, message } = await device.locate();
        if (!fix) {
            // The message comes back with the reading. Reading it off `device`
            // here would give the one from the render before the attempt, which
            // is null every time.
            setNotice(message);
            return;
        }
        if (!isPreciseEnoughToName(fix)) {
            setNotice(
                `Your location is only accurate to about ${Math.round(
                    fix.accuracyMetres
                )} m, which is not close enough to set a pick-up. Search for the address instead.`
            );
            return;
        }

        setResolving(true);
        try {
            const place = await reverseGeocode({
                latitude: fix.latitude,
                longitude: fix.longitude,
            }).unwrap();
            fill(
                {
                    name: place.name,
                    // The coordinate is the device's, not the provider's: the
                    // API echoes back the point it was sent rather than snapping
                    // it to the building it matched.
                    address: place.address || place.name,
                    latitude: fix.latitude,
                    longitude: fix.longitude,
                },
                place.address || place.name
            );
        } catch {
            setNotice(
                "We found you but could not name the street. Search for the address instead."
            );
        } finally {
            setResolving(false);
        }
    };

    /** Runs whichever row the keyboard is sitting on. */
    const activate = (index: number) => {
        if (offerCurrentLocation && index === 0) {
            void fillFromCurrentLocation();
            return;
        }
        const suggestion = suggestions[index - leadingRows];
        if (suggestion) void choose(suggestion.placeId, suggestion.description);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            setOpen(false);
            setActiveIndex(-1);
            return;
        }
        if (event.key === "Enter" && showList && activeIndex >= 0) {
            // Only when a row is highlighted, so Enter on a typed address still
            // submits the form it sits in.
            event.preventDefault();
            activate(activeIndex);
            return;
        }
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

        event.preventDefault();
        if (!open) setOpen(true);
        if (rowCount === 0) return;
        setActiveIndex((current) => {
            const step = event.key === "ArrowDown" ? 1 : -1;
            // Wraps, so holding one arrow never dead-ends at an edge.
            if (current < 0) return step === 1 ? 0 : rowCount - 1;
            return (current + step + rowCount) % rowCount;
        });
    };

    // Keeps the highlighted row in view when the list is longer than its box.
    useEffect(() => {
        if (activeIndex < 0) return;
        listRef.current?.children[activeIndex]?.scrollIntoView({
            block: "nearest",
        });
    }, [activeIndex]);

    const activeId = activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined;

    return (
        <div className={style.wrapper} ref={container}>
            <input
                required={required}
                className={`${className ?? ""} ${justFilled ? style.filled : ""}`.trim()}
                id={id}
                name={name}
                type="text"
                autoComplete="off"
                role="combobox"
                aria-expanded={showList}
                aria-controls={`${id}-suggestions`}
                aria-activedescendant={activeId}
                aria-autocomplete="list"
                value={text}
                placeholder={placeholder}
                disabled={resolving}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setOpen(true)}
            />

            {showList && (
                <ul
                    id={`${id}-suggestions`}
                    className={style.suggestions}
                    role="listbox"
                    ref={listRef}
                >
                    {offerCurrentLocation && (
                        <li
                            id={`${id}-option-0`}
                            role="option"
                            aria-selected={activeIndex === 0}
                            className={activeIndex === 0 ? style.active : undefined}
                        >
                            <button
                                type="button"
                                className={style.currentLocation}
                                disabled={device.isLocating || resolving}
                                onMouseEnter={() => setActiveIndex(0)}
                                onClick={() => void fillFromCurrentLocation()}
                            >
                                <span
                                    className={`${style.locationIcon} ${
                                        device.isLocating ? style.seeking : ""
                                    }`.trim()}
                                    aria-hidden="true"
                                >
                                    <MdMyLocation />
                                </span>
                                <span className={style.main}>
                                    {device.isLocating
                                        ? "Finding you…"
                                        : "Use my current location"}
                                </span>
                            </button>
                        </li>
                    )}

                    {suggestions.map((suggestion, index) => {
                        const row = index + leadingRows;
                        return (
                            <li
                                key={suggestion.placeId}
                                id={`${id}-option-${row}`}
                                role="option"
                                aria-selected={activeIndex === row}
                                className={
                                    activeIndex === row ? style.active : undefined
                                }
                            >
                                <button
                                    type="button"
                                    onMouseEnter={() => setActiveIndex(row)}
                                    onClick={() =>
                                        void choose(
                                            suggestion.placeId,
                                            suggestion.description
                                        )
                                    }
                                >
                                    <span className={style.main}>
                                        {suggestion.mainText}
                                    </span>
                                    {suggestion.secondaryText && (
                                        <span className={style.secondary}>
                                            {suggestion.secondaryText}
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* Why the location button did nothing. Kept under the field rather
                than in a toast: it is about this box, and the fix for it is in
                this box. */}
            {notice && (
                <p className={style.notice} role="status">
                    {notice}
                </p>
            )}

            {/* Only once there is enough text to have triggered a request, so
                an empty box does not sit there saying it is searching. */}
            {isFetching && query.trim().length >= 3 && (
                <span className={style.hint}>Searching…</span>
            )}
        </div>
    );
};

export default PlacesInput;
export { PlacesInput };
